import { Fragment, useEffect, useState } from "react";
import {
  Drawer,
  Form,
  Row,
  Col,
  Divider,
  Select,
  Input,
  Button,
  Space,
  DatePicker,
  message,
  InputNumber,
  Upload,
  Image,
  Segmented,
  Switch,
  Modal,
  Alert,
  Popover,
  Typography,
  Checkbox,
} from "antd";
import { MinusCircleOutlined, PlusOutlined, FilterOutlined } from "@ant-design/icons";
import type { RangePickerProps } from "antd/es/date-picker";
import type { UploadFile } from "antd/es/upload/interface";
import type { RcFile, UploadProps } from "antd/es/upload";

import { TableData } from "Utility/Types/status_identity_get_set";
import { useFilter } from "Utility/hooks/useFilter";
import { ReportRecord } from "Utility/Types/report";
import { CharacterVerify, CharacterVerifyEngNumOnly } from "Utility/RegExp";
import { Info } from "Utility/Types/management";
import moment from "moment";

import SelectorFooter from "Components/Shared/SelectorFooter";
import FiledFilter from "Components/Shared/FieldFilter";

import { setPDFVisible } from "Components/Report/reportSlice";
import { useTranslation } from "react-i18next";

// queries and redux
import {
  useReportList,
  useCreateReort,
  useInstanceReport,
  useModifyReort,
  useUploadLogo,
} from "Components/Report/queries";
import { useRepositoryDownload, useRepositoryList, useRepositoryDeleteFile } from "Components/DeviceBackup/queries";
import { useListCustomerInf } from "Components/Management/queries";
import { useDeviceStatus } from "Components/Dashboard/queries";
import { useAppDispatch, useAppSelector } from "store/hook";

const { Option } = Select;
const { Text } = Typography;

const initialFilter = {
  model: [],
  groups: [],
  city: [],
};

const getBase64 = (file: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

// export const periodicOptions = ["daily", "weekly", "monthly"] as const;
export const periodicOptions = [
  { label: "Daily", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
  { label: "Custom", value: "custom" },
] as const;

const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
export const periodicReportDayOptions = {
  daily: [{ label: "Daily", value: 1 }],
  weekly: Array.from({ length: 7 }).map((_, index) => ({ label: days[index], value: index + 1 })),
  monthly: Array.from({ length: 28 }).map((_, index) => ({ label: `Day ${index + 1}`, value: index + 1 })),
  custom: [{ label: "Custom", value: 86400 }],
};

export const periodicReportHourOptions = Array.from({ length: 24 }).map((_, index) => index);

export const _unitOptions = {
  // { label: "Hour", value: 3600 }, // 60* 60 = 3600
  // { label: "Day", value: 86400 }, //24*60*60= 86400
  // { label: "Week", value: 604800 }, //7*24*60*60 = 604800
  // { label: "Month", value: 2592000 }, //30*24*60*60 = 2592000
  daily: [{ label: "Hour", value: 3600 }],
  weekly: [{ label: "Day", value: 86400 }],
  monthly: [
    { label: "Week", value: 604800 },
    { label: "Day", value: 86400 },
  ],
  overMonth: [
    { label: "Day", value: 86400 },
    { label: "Week", value: 604800 },
    { label: "Month", value: 2592000 },
  ],
  custom: [
    { label: "Hour", value: 3600 },
    { label: "Day", value: 86400 },
    { label: "Week", value: 604800 },
    { label: "Month", value: 2592000 },
  ],
} as const;

const accountChartTypeOptions = [
  { label: "Bar", value: "bar" },
  { label: "Line", value: "line" },
  { label: "Pie", value: "pie" },
  { label: "Table-Top5", value: "table-top5" },
  { label: "Table-Top10", value: "table-top10" },
];

const deviceChartTypeOptions = [
  { label: "Bar", value: "bar" },
  { label: "Line", value: "line" },
];

const line_bar_options = [
  { label: "Health All", value: "health_all" },
  { label: "Health Offline", value: "health_offline" },
  { label: "Health Critical", value: "health_critical" },
  { label: "Health Warning", value: "health_warning" },
  { label: "Signal All", value: "signal_all" },
  { label: "Signal Poor", value: "signal_poor" },
  { label: "Signal Fair", value: "signal_fair" },
  { label: "Signal Good", value: "signal_good" },
  { label: "Signal", value: "signal" },
  { label: "Cellular All", value: "cellular_all", available: "device" },
  { label: "Cellular Tx", value: "cellular_tx", available: "device" },
  { label: "Cellular Rx", value: "cellular_rx", available: "device" },
  { label: "WAN All", value: "wan_all", available: "device" },
  { label: "WAN Tx", value: "wan_tx", available: "device" },
  { label: "WAN Rx", value: "wan_rx", available: "device" },
];

const table_options = [
  { label: "Health All", value: "health_all" },
  { label: "Signal All", value: "signal_all" },
  { label: "Cellular All", value: "cellular_all" },
  { label: "WAN All", value: "wan_all" },
];

const pie_options = [
  { label: "Current Health", value: "current_health" },
  { label: "Current Signal", value: "current_signal" },
  { label: "Current Models", value: "current_models" },
];

const accountDefaultValue = {
  account_parameter: [
    {
      type: "pie",
      parameter: [pie_options[0].value, pie_options[1].value, pie_options[2].value],
    },
    {
      type: "bar",
      parameter: [line_bar_options[0].value, line_bar_options[4].value],
    },
    { type: "table-top10", parameter: [table_options[0].value, table_options[1].value], top_num: 10 },
    { type: "table-top10", parameter: [table_options[2].value, table_options[3].value], top_num: 10 },
  ],
};

const deviceDefaultValue = {
  device_parameter: [
    { type: "bar", parameter: [line_bar_options[0].value, line_bar_options[4].value] },
    {
      type: "line",
      parameter: [
        line_bar_options[10].value,
        line_bar_options[11].value,
        line_bar_options[13].value,
        line_bar_options[14].value,
        line_bar_options[8].value,
        line_bar_options[0].value,
      ],
    },
  ],
};

const parameterOptions = {
  pie: pie_options,
  line: line_bar_options,
  bar: line_bar_options,
  "table-top5": table_options,
  "table-top10": table_options,
};

interface TemplateProp extends React.ComponentProps<typeof Button> {
  record?: ReportRecord;
  setRecord: (arg: ReportRecord | undefined) => void;
  setPDF: (arg: File) => void;
  templateVisible: boolean;
  setTemplateVisible: (arg: boolean) => void;
}

export const disabledStartDate: RangePickerProps["disabledDate"] = (current) => {
  // Can not select days before today and today
  return current && current < moment().subtract(6, "months");
};

const chartNumberValidator = (chartType: string, number: number) => {
  if (chartType === "line" && number > 6) {
    return Promise.reject(new Error("Max: 6"));
  }

  if (chartType === "bar" && number > 4) {
    return Promise.reject(new Error("Max: 4"));
  }

  if (chartType === "pie" && number > 3) {
    return Promise.reject(new Error("Max: 3"));
  }

  if (chartType === "table-top5" && number > 4) {
    return Promise.reject(new Error("Max: 4"));
  }

  if (chartType === "table-top10" && number > 2) {
    return Promise.reject(new Error("Max: 2"));
  }
};

const ReportTemplate = ({ record, setRecord, setPDF, templateVisible, setTemplateVisible }: TemplateProp) => {
  const disp = useAppDispatch();
  const { level, stateCid, isLogin, loginCid } = useAppSelector((status) => status.global);
  const cid = level === "super_super" && stateCid !== "" ? stateCid : loginCid;
  const [accountFilterVisible, setAccountFilterVisible] = useState(false);
  const [deviceFilterVisible, setDeviceFilterVisible] = useState(false);
  const isSuper = level === "super_super" && stateCid === "";
  const { data: CustomerInfo } = useListCustomerInf(level, stateCid, isLogin);
  const { data: reportTable, isLoading: reportListLoading, isRefetching } = useReportList(cid, stateCid);
  const report_data = reportTable?.response?.[0]?.list || [];
  const [reportDayOptions, setReportDayOptions] = useState(periodicReportDayOptions[periodicOptions[0].value]);
  const [value, setValue] = useState<string | number>("Basic");
  const [unitOptions, setUnitOptions] = useState<any>(_unitOptions[periodicOptions[3].value]);
  const [mailEnable, setMailEnable] = useState(false);
  const [accountChartType, setAccountChartType] = useState<Record<number, keyof typeof parameterOptions>>(() =>
    accountDefaultValue.account_parameter.reduce((a, b, i) => ({ ...a, [i]: b.type }), {})
  );

  const [deviceChartType, setDeviceChartType] = useState<Record<number, keyof typeof parameterOptions>>(() =>
    deviceDefaultValue.device_parameter.reduce((a, b, i) => ({ ...a, [i]: b.type }), {})
  );
  const { mutateAsync: deleteFileMutation } = useRepositoryDeleteFile(stateCid);
  const [form] = Form.useForm();
  const { data: deviceStatus } = useDeviceStatus(level, stateCid, loginCid);
  const [demo, setDemo] = useState<boolean | undefined>(undefined);
  const tableData = deviceStatus?.response?.tableData || [];
  const [accountTableData, setAccountTableData] = useState<Record<keyof TableData, any>[]>([]);
  const [deviceTableData, setDeviceTableData] = useState<Record<keyof TableData, any>[]>([]);
  const { mutateAsync: createReportMutation, isLoading: createReportLoading } = useCreateReort(stateCid);
  const { mutateAsync: createInstanceReportMutation, isLoading: instanceReportLoading } = useInstanceReport(stateCid);
  const { mutateAsync: modifyReportMutation, isLoading: modifyReportLoading } = useModifyReort(stateCid);
  const { mutateAsync: uploadLogoMutation, isLoading: uploadImageLoading } = useUploadLogo(stateCid);
  const { mutateAsync: downloadMutation } = useRepositoryDownload();
  const { data: repositoryList = [] } = useRepositoryList(level, stateCid, loginCid);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [hasInitialImage, setHasInitialImage] = useState(false);
  const editCreateLoading = createReportLoading || modifyReportLoading || uploadImageLoading;
  const { t } = useTranslation();
  const [fileList, setFileList] = useState<any[]>([]);
  const [periodic, setPeriodic] = useState("daily");
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const [accountFilter, setAccountFilter] = useState<Record<string, string[]>>({
    ...initialFilter,
  });
  const [deviceFilter, setDeviceFilter] = useState<Record<string, string[]>>({ ...initialFilter });
  const [autoSelectAll, setAutoSelectAll] = useState(false);
  const [selectedCid, setSelectedCid] = useState("");

  const [validPeriodicOpts, setValidPeriodicOpts] = useState<any[]>([
    { label: "Daily", value: "daily" },
    { label: "Weekly", value: "weekly" },
    { label: "Monthly", value: "monthly" },
    { label: "Custom", value: "custom" },
  ]);
  const [start, setStart] = useState<any>(null);
  const [end, setEnd] = useState<any>(null);
  const [isInputting, setIsInputting] = useState(false);

  useFilter({
    filter: accountFilter,
    tableData,
    setDeviceTableData: setAccountTableData,
  });

  useFilter({
    filter: deviceFilter,
    tableData,
    setDeviceTableData: setDeviceTableData,
  });

  const disabledEndDate: RangePickerProps["disabledDate"] = (current) => {
    const start = form.getFieldValue("start_utc");
    // const tooLate = (start && current.diff(start, "days") === 0) as boolean;
    // Can not select days before today and today
    // return (current && current < start) || tooLate;

    return current && current < start?.startOf("day");
  };

  const handleChange: UploadProps["onChange"] = ({ fileList: newFileList }) => setFileList(newFileList);

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as RcFile);
    }

    setPreviewImage(file.url || (file.preview as string));
    setPreviewVisible(true);
    // setPreviewTitle(file.name || file.url!.substring(file.url!.lastIndexOf("/") + 1));
  };

  const props: UploadProps = {
    accept: "image/png, image/jpeg",
    onRemove: (file: any) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    onChange: handleChange,
    onPreview: handlePreview,
    listType: "picture-card",
    fileList,
  };

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const onFinishFailed = () => {
    setErrorMessage("Invalided Values, Please Check Errors.");
  };

  const periodicFn = (days: number) => {
    // 1 天顯示日
    // 2 ~ 6 天顯示日、custom
    // 7 ~ 29 天顯示日、周、custom
    // 30 天以上顯示日、周、月、custom
    if (days === 1) {
      setValidPeriodicOpts([{ label: "Daily", value: "daily" }]);
    } else if (1 < days && days <= 6) {
      setValidPeriodicOpts([
        { label: "Daily", value: "daily" },
        { label: "Custom", value: "custom" },
      ]);
    } else if (6 < days && days <= 29) {
      setValidPeriodicOpts([
        { label: "Daily", value: "daily" },
        { label: "Weekly", value: "weekly" },
        { label: "Custom", value: "custom" },
      ]);
    } else if (29 < days) {
      setValidPeriodicOpts([
        { label: "Daily", value: "daily" },
        { label: "Weekly", value: "weekly" },
        { label: "Monthly", value: "monthly" },
        { label: "Custom", value: "custom" },
      ]);
    } else {
      setValidPeriodicOpts([
        { label: "Daily", value: "daily" },
        { label: "Weekly", value: "weekly" },
        { label: "Monthly", value: "monthly" },
        { label: "Custom", value: "custom" },
      ]);
    }
  };

  //set field when editing

  useEffect(() => {
    form.resetFields();

    // ********************** Create template START **********************
    if (!record) {
      setReportDayOptions(periodicReportDayOptions["daily"]);
      setPeriodic("daily");
      setUnitOptions(customPeriodOnChange(1));
      setFileList([]);
      setAccountDefault();
      setDeviceDefault();
      setMailEnable(false);
      setAutoSelectAll(false);
      return;
    }
    // ********************** Create template END **********************

    const Logo = repositoryList[0]?.list?.find(({ name }) => name === `${record.file_name}.logo`);
    if (Logo) {
      downloadMutation({ cid, name: Logo.name, type: "REPORT" }).then(async ({ data }) => {
        const url = await getBase64(data as RcFile);
        const objectURL = URL.createObjectURL(data);
        setHasInitialImage(true);
        // setPreviewTitle("logo.png");
        setFileList([{ originFileObj: data, url: objectURL }]);
        setPreviewImage(url);
      });
    }

    const currentPeriodic = record.report.duration.default_report?.periodic;
    const start_utc = record.report.duration.start_utc;
    const end_utc = record.report.duration.end_utc;
    const days = Math.ceil((end_utc * 1000 - start_utc * 1000) / (1000 * 60 * 60 * 24));

    setAutoSelectAll(record.report.filter.account_level.device[0] === "All" ? true : false);
    setReportDayOptions(periodicReportDayOptions[currentPeriodic]);
    setStart(start_utc * 1000);
    setPeriodic(record.report.duration.default ? currentPeriodic : "custom");
    setUnitOptions(
      record.report.duration.default
        ? _unitOptions[currentPeriodic]
        : customPeriodOnChange(record.report.duration.customize_report?.periodic / 86400 || 1)
    );
    setMailEnable(record.mail?.active || false);
    setAccountChartType(
      record.report.page.account_level.reduce((acc, curr, index) => {
        let type = curr.chart[0].type;
        if (curr.chart[0].type === "table") {
          //@ts-ignore
          if (curr.chart[0].top_num === 10) {
            type = "table-top10";
          } else {
            type = "table-top5";
          }
        }

        return { ...acc, [index]: type };
      }, {})
    );

    setDeviceChartType(
      record.report.page.device_level.reduce((acc, curr, index) => {
        let type = curr.chart[0].type;
        if (curr.chart[0].type === "table") {
          //@ts-ignore
          if (curr.chart[0].top_num === 10) {
            type = "table-top10";
          } else {
            type = "table-top5";
          }
        }

        return { ...acc, [index]: type };
      }, {})
    );

    periodicFn(days);

    form.setFieldsValue({
      company: record.company,
      active: record.active || false,
      name: record.name,
      description: record.description,
      file_name: record.file_name,
      start_utc: start_utc ? moment(start_utc * 1000) : undefined,
      end_utc: end_utc ? moment(end_utc * 1000) : undefined,
      periodic: record.report.duration.default ? currentPeriodic : "custom",
      report_day: record.report.duration.default_report?.report_day,
      custom_periodic: record.report.duration.customize_report?.periodic / 86400,
      unit: record.report.duration.unit,
      account_devices: record.report.filter.account_level.device,
      //@ts-ignore
      account_parameter: record.report.page.account_level.reduce((acc, curr) => {
        let type = curr.chart[0].type;
        if (curr.chart[0].type === "table") {
          //@ts-ignore
          if (curr.chart[0].top_num === 10) {
            type = "table-top10";
          } else {
            type = "table-top5";
          }
        }
        return [...acc, { type, parameter: curr.chart.map((c) => c.parameter) }];
      }, []),

      device_devices: record.report.filter.device_level.device,
      //@ts-ignore
      device_parameter: record.report.page.device_level.reduce((acc, curr) => {
        return [...acc, { type: curr.chart[0].type, parameter: curr.chart.map((c) => c.parameter) }];
      }, []),

      mail_active: record.mail?.active || false,
      mail_to: record.mail?.mail_to,
      mail_cc: record.mail?.mail_cc,
    });
  }, [record, templateVisible]);

  const setAccountDefault = () => {
    form.setFieldsValue(accountDefaultValue);
  };

  const setDeviceDefault = () => {
    form.setFieldsValue(deviceDefaultValue);
  };

  const customPeriodOnChange = (customPeriod: number) => {
    // 1 天顯示日
    // 2 ~ 6 天顯示日、custom
    // 7 ~ 29 天顯示日、周、custom
    // 30 天以上顯示日、周、月、custom
    if (customPeriod <= 1) {
      const unitOption = _unitOptions["daily"];
      setUnitOptions(unitOption);
      form.setFieldsValue({ unit: unitOption[0].value });
      return unitOption;
    } else if (1 < customPeriod && customPeriod <= 6) {
      const unitOption = _unitOptions["weekly"];
      setUnitOptions(_unitOptions["weekly"]);
      form.setFieldsValue({ unit: unitOption[0].value });
      return unitOption;
    } else if (6 < customPeriod && customPeriod <= 29) {
      const unitOption = _unitOptions["monthly"];
      setUnitOptions(_unitOptions["monthly"]);
      form.setFieldsValue({ unit: unitOption[0].value });
      return unitOption;
    } else {
      const unitOption = _unitOptions["overMonth"];
      setUnitOptions(_unitOptions["overMonth"]);
      form.setFieldsValue({ unit: unitOption[0].value });
      return unitOption;
    }
  };

  const onFinish = async (value: any) => {
    setErrorMessage(undefined);
    if (fileList.length) {
      await uploadLogoMutation({ cid, fileList, file_name: value.file_name }).then((res) => console.log(res));
    } else if (!fileList.length && hasInitialImage) {
      deleteFileMutation({
        level,
        stateCid,
        loginCid,
        record: { cid, name: `${value.file_name}.logo`, type: "REPORT" },
      });
    }

    if (value.periodic === "custom") {
      value.periodic = record?.report?.duration?.default_report?.periodic || "weekly";
    }

    const account_parameter = value.account_parameter.map((item: any) => ({
      plot: { row: item.parameter.length, col: 1 },
      chart: item.parameter.map((p: any) => {
        if (item.type.includes("table"))
          return { parameter: p, type: "table", top_num: item.type === "table-top5" ? 5 : 10 };
        return { parameter: p, type: item.type };
      }),
    }));

    const device_parameter = value.device_parameter.map((item: any) => ({
      plot: { row: item.parameter.length, col: 1 },
      chart: item.parameter.map((p: any) => ({ parameter: p, type: item.type })),
    }));

    const duration = {
      // if start/end time id not given, start from now and the effective period is endless
      start_utc: Math.round(value["start_utc"]?.startOf("day")?.format("X") || null),
      end_utc: Math.round(value["end_utc"]?.endOf("day")?.format("X") || null),
    };

    const param = {
      ...value,
      _id: record?._id,
      duration,
      demo,
      account_parameter,
      device_parameter,
      cid,
      customStatus: periodic,
      mail_cc: value.mail_cc ?? [], // 如果是 undefined 就給 []
    };

    if (demo) {
      createInstanceReportMutation({ ...param, demo: true }).then((res) => {
        //@ts-ignore
        setPDF(res.data);
        disp(setPDFVisible(true));
      });
    } else {
      if (!record) {
        createReportMutation(param)
          .then((res) => {
            message.success(t("ISMS.Success"));
            setTemplateVisible(false);
          })
          .catch((error) => {
            message.error(t("ISMS.Fail"));
          });
      } else {
        //modify report need provode new/old report name
        modifyReportMutation(param).then((res) => message.success(t("ISMS.Success")));
      }
    }
  };

  // get template name and file name of report
  let report_template_name = new Set();
  let report_file_name = new Set();
  report_data?.map((item: any) => {
    report_template_name.add(item.name);
    report_file_name.add(item.file_name);
  });

  return (
    <>
      <Drawer
        width="70%"
        getContainer={false}
        destroyOnClose
        title={`${record ? "Edit" : "New "} Template`}
        visible={templateVisible}
        onClose={() => {
          setTemplateVisible(false);
          setDemo(undefined);
          setPeriodic("daily");
          setPreviewImage("");
          setHasInitialImage(false);
          setRecord(undefined);
          setValue("Basic");
          setFileList([]);
          setErrorMessage(undefined);
          setValidPeriodicOpts([
            { label: "Daily", value: "daily" },
            { label: "Weekly", value: "weekly" },
            { label: "Monthly", value: "monthly" },
            { label: "Custom", value: "custom" },
          ]);
          setStart(null);
          setEnd(null);
        }}
        headerStyle={{ display: "flex", justifyContent: "space-between" }}
        extra={
          <Space style={{ marginRight: "2rem" }}>
            <Button
              loading={instanceReportLoading}
              onClick={() => {
                setDemo(true);
                form.submit();
              }}
            >
              View Layout
            </Button>

            <Button
              loading={editCreateLoading}
              onClick={() => {
                setDemo(false);
                form.submit();
              }}
              type="primary"
            >
              Save
            </Button>
          </Space>
        }
      >
        {errorMessage && (
          <>
            <Alert
              onClose={() => setErrorMessage(undefined)}
              message={errorMessage}
              type="error"
              showIcon
              closable
              className="mb-3"
            />
          </>
        )}

        <Form form={form} onFinish={onFinish} onFinishFailed={onFinishFailed} layout="vertical">
          <Row gutter={24}>
            <Col span={24} style={{ display: "none" }}>
              <Segmented options={["Basic", "Advanced"]} value={value} onChange={setValue} />
            </Col>

            <Form.Item style={{ display: "none" }} name="active" valuePropName="checked" initialValue={true}>
              <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
            </Form.Item>
          </Row>
          <div className={"formwrap"}>
            <Row gutter={24} justify="space-around" style={value !== "Basic" ? { display: "none" } : {}}>
              <Col xs={24} sm={24} md={24} lg={24} xl={11}>
                <h2>Report Template</h2>
                <Divider />

                <Form.Item
                  name={"name"}
                  label="Template Name"
                  rules={[
                    { required: true, message: "" },
                    CharacterVerify,
                    () => ({
                      validator(rule, value) {
                        if (report_template_name.has(value)) {
                          if (value === record?.name) return Promise.resolve();
                          return Promise.reject("Duplicate!");
                        } else {
                          return Promise.resolve();
                        }
                      },
                    }),
                  ]}
                >
                  <Input placeholder={"Input Report Template Name"} maxLength={30} />
                </Form.Item>

                <Form.Item
                  name={"file_name"}
                  label="File Name"
                  rules={[
                    { required: true, message: "" },
                    CharacterVerifyEngNumOnly,
                    () => ({
                      validator(rule, value) {
                        if (report_file_name.has(value)) {
                          if (Boolean(record)) return Promise.resolve();
                          return Promise.reject("Duplicate!");
                        } else {
                          return Promise.resolve();
                        }
                      },
                    }),
                  ]}
                >
                  <Input disabled={Boolean(record)} placeholder={"File Name for your PDF"} maxLength={30} />
                </Form.Item>

                <Form.Item
                  name={"description"}
                  label="Description"
                  rules={[{ required: true, message: "" }, CharacterVerify]}
                >
                  <Input placeholder={"Input some description..."} maxLength={120} />
                </Form.Item>

                <Row gutter={24}>
                  <Col xs={24} sm={24} md={24} lg={24} xl={12}>
                    <Form.Item name={"start_utc"} label="Start">
                      <DatePicker
                        disabledDate={disabledStartDate}
                        format="YYYY-MM-DD"
                        showTime={false}
                        style={{ width: "100%" }}
                        onChange={(value) => {
                          form.resetFields(["end_utc", "periodic", "unit"]);
                          form.setFieldsValue({ report_day: 1 });
                          setReportDayOptions(periodicReportDayOptions["daily"]);
                          setUnitOptions(customPeriodOnChange(1));
                          setValidPeriodicOpts([
                            { label: "Daily", value: "daily" },
                            { label: "Weekly", value: "weekly" },
                            { label: "Monthly", value: "monthly" },
                            { label: "Custom", value: "custom" },
                          ]);
                          setStart(value?.valueOf());
                          setPeriodic("daily");
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={24} lg={24} xl={12}>
                    <Form.Item name={"end_utc"} label="End">
                      <DatePicker
                        disabledDate={disabledEndDate}
                        format="YYYY-MM-DD"
                        showTime={false}
                        style={{ width: "100%" }}
                        onChange={(value) => {
                          if (value) {
                            // @ts-ignore
                            const days = Math.ceil((value.valueOf() - start) / (1000 * 60 * 60 * 24));
                            periodicFn(days);
                            form.resetFields(["periodic", "unit"]);
                            form.setFieldsValue({ report_day: 1 });
                            setReportDayOptions(periodicReportDayOptions["daily"]);
                            setUnitOptions(customPeriodOnChange(1));
                            setPeriodic("daily");
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col xs={24} sm={24} md={24} lg={24} xl={12}>
                    <Form.Item
                      name={"periodic"}
                      label="Periodic"
                      initialValue={periodicOptions[0].value}
                      rules={[{ required: true, message: "" }, CharacterVerify]}
                    >
                      <Select
                        onChange={(value: keyof typeof periodicReportDayOptions) => {
                          //when period changes, report-options & unit-options should change accordingly
                          setReportDayOptions(periodicReportDayOptions[value]);
                          form.setFieldsValue({ report_day: 1 });

                          setUnitOptions(_unitOptions[value]);
                          setPeriodic(value);
                          //reset unit to correct default
                          form.setFieldsValue({ unit: _unitOptions[value][0]?.value });
                          if (value === "custom") {
                            const custom_period = record?.report?.duration?.customize_report?.periodic;
                            if (custom_period) {
                              form.setFieldsValue({ custom_periodic: custom_period / 86400 });
                              customPeriodOnChange(custom_period / 86400);
                            } else {
                              form.resetFields(["custom_periodic"]);
                              customPeriodOnChange(1);
                            }
                          }
                        }}
                      >
                        {validPeriodicOpts.map((opt) => (
                          <Option key={opt.value} value={opt.value}>
                            {opt.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  {periodic !== "custom" ? (
                    <Col xs={24} sm={24} md={24} lg={24} xl={12}>
                      <Form.Item
                        name={"report_day"}
                        label="Receive Day"
                        initialValue={reportDayOptions[0]?.value}
                        rules={[{ required: true, message: "" }, CharacterVerify]}
                      >
                        <Select>
                          {reportDayOptions.map((opt) => (
                            <Option key={opt.value} value={opt.value}>
                              {opt.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  ) : (
                    <Col xs={24} sm={24} md={24} lg={24} xl={12}>
                      <Form.Item name={"custom_periodic"} label="Period Day" initialValue={1}>
                        <InputNumber onChange={customPeriodOnChange} style={{ width: "100%" }} min={1} max={30} />
                      </Form.Item>
                    </Col>
                  )}
                </Row>
                <Form.Item name={"unit"} label="Unit" initialValue={unitOptions[0].value}>
                  <Select>
                    {unitOptions.map((opt: any) => {
                      return (
                        <Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Option>
                      );
                    })}
                  </Select>
                </Form.Item>

                <Row gutter={24}>
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Form.Item
                      style={{ minHeight: "150px" }}
                      name="dragger"
                      valuePropName="fileList"
                      getValueFromEvent={normFile}
                    >
                      <>
                        <Upload {...props} maxCount={1}>
                          {fileList.length >= 1 ? null : (
                            <Space direction="vertical">
                              <PlusOutlined />
                              <Text style={{ marginTop: 8 }}>Upload Logo</Text>
                            </Space>
                          )}
                        </Upload>
                        <Modal visible={previewVisible} footer={null} onCancel={() => setPreviewVisible(false)}>
                          <Image style={{ width: "100%" }} src={previewImage} />
                        </Modal>
                      </>
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
              <Col xs={24} sm={24} md={24} lg={24} xl={11}>
                {isSuper && (
                  <>
                    <h2>Customer</h2>
                    <Divider />

                    <Form.Item
                      name={"company"}
                      label="Customer"
                      initialValue={[]}
                      rules={[{ required: true, message: "Must select a company!" }]}
                    >
                      <Select
                        maxTagCount="responsive"
                        onChange={(value) => {
                          if (isSuper && value !== "") {
                            //filter devices based on selected Cid
                            setAccountFilter((prev) => ({ cid: [value] }));
                            setDeviceFilter((prev) => ({ cid: [value] }));
                            setSelectedCid(value);
                            setAutoSelectAll(false);
                            form.resetFields(["account_devices", "device_devices"]);
                          }
                        }}
                      >
                        {CustomerInfo?.response?.map((cust: Info) => (
                          <Option key={cust?.cid} value={cust?.cid}>
                            {cust?.inf_list?.company}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </>
                )}
                <h2>Account Level</h2>
                <Divider />
                <Form.Item
                  name={"account_devices"}
                  label={
                    <Fragment>
                      Device(Account-Level)
                      <Popover
                        content={
                          <FiledFilter selectedCid={selectedCid} filter={accountFilter} setFilter={setAccountFilter} />
                        }
                        trigger="click"
                        visible={accountFilterVisible}
                        onVisibleChange={(visible) => setAccountFilterVisible(visible)}
                      >
                        <Button size="small" type="text" icon={<FilterOutlined />}></Button>
                      </Popover>
                    </Fragment>
                  }
                  initialValue={isSuper ? [] : accountTableData.map((o) => o.id)}
                  rules={[{ required: true, message: "" }]}
                >
                  <Select
                    placeholder="Select"
                    mode="multiple"
                    allowClear
                    autoClearSearchValue={false}
                    optionFilterProp="label"
                    maxTagCount="responsive"
                    onSearch={(value: string) => {
                      value.length > 0 ? setIsInputting(true) : setIsInputting(false);
                    }}
                    onClear={() => {
                      setIsInputting(false);
                    }}
                    onFocus={() => {
                      setIsInputting(false);
                    }}
                    dropdownRender={(menu) =>
                      SelectorFooter({
                        menu,
                        selectAll: () =>
                          form.setFieldsValue({
                            account_devices: accountTableData.map((o) => o.id),
                          }),
                        clearAll: () =>
                          form.setFieldsValue({
                            account_devices: [],
                          }),
                        isInputting,
                      })
                    }
                  >
                    {accountTableData.map((data) => (
                      <Option key={data.name_or_id} value={data.id} label={data.name_or_id}>
                        {data.name_or_id}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <h2>Device Level</h2>
                <Divider />
                {/* <h3>Device & Parameter</h3> */}
                <Form.Item
                  name={"device_devices"}
                  initialValue={[]}
                  label={
                    <Fragment>
                      Device(Device-Level)
                      <Popover
                        content={
                          <FiledFilter selectedCid={selectedCid} filter={deviceFilter} setFilter={setDeviceFilter} />
                        }
                        trigger="click"
                        visible={deviceFilterVisible}
                        onVisibleChange={(visible) => setDeviceFilterVisible(visible)}
                      >
                        <Button size="small" type="text" icon={<FilterOutlined />}></Button>
                      </Popover>
                    </Fragment>
                  }
                  rules={[
                    {
                      validator: async (_, value) => {
                        return value.length > 10 && Promise.reject(new Error("Max: 10"));
                      },
                    },
                  ]}
                >
                  <Select
                    placeholder="Select"
                    mode="multiple"
                    allowClear
                    autoClearSearchValue={false}
                    optionFilterProp="label"
                    maxTagCount="responsive"
                    onSearch={(value: string) => {
                      value.length > 0 ? setIsInputting(true) : setIsInputting(false);
                    }}
                    onClear={() => {
                      setIsInputting(false);
                    }}
                    onFocus={() => {
                      setIsInputting(false);
                    }}
                    dropdownRender={(menu) =>
                      SelectorFooter({
                        menu,
                        selectAll: () => {
                          form.setFieldsValue({
                            device_devices: deviceTableData.map((o) => o.id),
                          });
                          form.validateFields(["device_devices"]); // 重新驗證
                        },
                        clearAll: () =>
                          form.setFieldsValue({
                            device_devices: [],
                          }),
                        isInputting,
                      })
                    }
                  >
                    {deviceTableData.map((data) => (
                      <Option key={data.name_or_id} value={data.id} label={data.name_or_id}>
                        {data.name_or_id}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <h2>Email</h2>
                <Divider />
                <Form.Item name="mail_active" label={"Mail Enable"} initialValue={false}>
                  <Select onChange={(value) => setMailEnable(value)} placeholder="Enable/Disable">
                    <Option value={true}>Enable</Option>
                    <Option value={false}>Disable</Option>
                  </Select>
                </Form.Item>

                {mailEnable && (
                  <>
                    {/* <Form.Item
                      label={t("ISMS.MailServer")}
                      name="mail_server"
                      rules={[{ required: mailEnable }, CharacterVerify]}
                    >
                      <Input placeholder={t("ISMS.InputServer")} maxLength={50} />
                    </Form.Item>
                    <Form.Item label={t("ISMS.MailPort")} name="mail_port" rules={[{ required: mailEnable }]}>
                      <InputNumber
                        max={999999999999999999}
                        min={0}
                        style={{ width: "100%" }}
                        placeholder={t("ISMS.InputPort")}
                      />
                    </Form.Item>
                    <Form.Item
                      label={t("ISMS.MailToken")}
                      name="token"
                      rules={[{ required: mailEnable }, CharacterVerify]}
                    >
                      <Input.Password placeholder={t("ISMS.Input Token")} autoComplete="new-password" maxLength={50} />
                    </Form.Item> */}
                    {/* <Form.Item
                      label={t("ISMS.MailFrom")}
                      name="mail_from"
                      rules={[{ required: mailEnable }, { type: "email", message: "Invalid!!" }]}
                    >
                      <Input placeholder={t("ISMS.MailAddress")} autoComplete="new-password" maxLength={50} />
                    </Form.Item> */}

                    <Form.Item label={t("ISMS.MailTo")} name="mail_to" rules={[{ required: mailEnable }]}>
                      <Select
                        placeholder={t("ISMS.MailAddress(multiple)")}
                        mode="tags"
                        style={{ width: "100%" }}
                        maxTagCount={1}
                      />
                    </Form.Item>
                    <Form.Item
                      label={t("ISMS.Mail CC")}
                      name="mail_cc"
                      // rules={[{ required: mailEnable }]}
                    >
                      <Select
                        placeholder={t("ISMS.MailAddress(multiple)")}
                        mode="tags"
                        style={{ width: "100%" }}
                        maxTagCount={1}
                      />
                    </Form.Item>
                  </>
                )}
              </Col>
            </Row>
            <Row gutter={24} justify={"space-around"} style={value !== "Advanced" ? { display: "none" } : {}}>
              <Col xs={24} sm={24} md={24} lg={24} xl={11}>
                <Space size={1}>
                  <h2>Account Level Parameter</h2>
                  <Button
                    type="text"
                    size="small"
                    onClick={() => {
                      setAccountDefault();
                      // setAccountChartValidation({});
                    }}
                  >
                    Set Default
                  </Button>
                </Space>

                <Divider />

                <Form.List name="account_parameter">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map((field) => (
                        <Row gutter={24} justify="space-around" align="middle" key={field.key}>
                          <Col xs={24} sm={24} md={24} lg={24} xl={6} style={{ paddingRight: 0, paddingLeft: 0 }}>
                            <Form.Item
                              {...field}
                              label="Chart Type"
                              name={[field.name, "type"]}
                              rules={[{ required: true, message: "required" }]}
                              // initialValue={"bar"}
                            >
                              <Select
                                placeholder="Select"
                                onChange={(value: keyof typeof parameterOptions) => {
                                  setAccountChartType({ ...accountChartType, [field.key]: value });
                                  form.resetFields([["account_parameter", field.name, "parameter"]]);
                                }}
                              >
                                {accountChartTypeOptions.map((opt, index) => (
                                  <Option key={index} value={opt.value}>
                                    {opt.label}
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={24} md={24} lg={24} xl={12} style={{ paddingRight: 0, paddingLeft: 0 }}>
                            <Form.Item
                              {...field}
                              label="Parameter"
                              name={[field.name, "parameter"]}
                              rules={[
                                { required: true, message: "required" },
                                {
                                  validator: async (_, value) => {
                                    return chartNumberValidator(accountChartType?.[field.key], value.length);
                                  },
                                },
                              ]}
                              // validateStatus={accountChartValidation?.[field.key]?.validateStatus || "success"}
                              // help={accountChartValidation?.[field.key]?.errorMsg}
                            >
                              <Select
                                placeholder="Select"
                                mode="multiple"
                                maxTagCount="responsive"
                                // onChange={(value: keyof typeof parameterOptions) => {
                                //   setAccountChartValidation({
                                //     ...accountChartValidation,
                                //     [field.key]: validateChartNumber(
                                //       accountChartType[field.key] || "bar",
                                //       value.length
                                //     ),
                                //   });
                                // }}
                              >
                                {parameterOptions[accountChartType?.[field.key] || "bar"]
                                  ?.filter((i, index) => {
                                    if (
                                      accountChartType?.[field.key] === "line" ||
                                      accountChartType?.[field.key] === "bar"
                                    ) {
                                      return index < 9 ? true : false;
                                    } else return true;
                                  })
                                  ?.map((option, index) => {
                                    return (
                                      <Option key={index} value={option.value}>
                                        {option.label}
                                      </Option>
                                    );
                                  })}
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={24} md={24} lg={24} xl={1} className="pr-0 pl-0 mb-2">
                            <Button
                              icon={<MinusCircleOutlined />}
                              block
                              danger
                              type="text"
                              onClick={() => remove(field.name)}
                            ></Button>
                          </Col>
                        </Row>
                      ))}

                      <Form.Item>
                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                          Add Page
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form.List>
              </Col>
              <Col xs={24} sm={24} md={24} lg={24} xl={11}>
                {/* <h2>Device Level Parameter</h2>
                 */}

                <Space size={1}>
                  <h2>Device Level Parameter</h2>
                  <Button
                    type="text"
                    size="small"
                    onClick={() => {
                      setDeviceDefault();
                      // setDeviceChartValidation({});
                    }}
                  >
                    Set Default
                  </Button>
                </Space>
                <Divider />

                <Form.List name="device_parameter">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map((field) => (
                        <Row gutter={24} justify="space-around" align="middle" key={field.key}>
                          <Col xs={24} sm={24} md={24} lg={24} xl={6} style={{ paddingRight: 0, paddingLeft: 0 }}>
                            <Form.Item
                              {...field}
                              label="Chart Type"
                              name={[field.name, "type"]}
                              rules={[{ required: true, message: "required" }]}
                              // initialValue={"bar"}
                            >
                              <Select
                                placeholder="Select"
                                onChange={(value: keyof typeof parameterOptions) => {
                                  setDeviceChartType({ ...deviceChartType, [field.key]: value });
                                  form.resetFields([["device_parameter", field.name, "parameter"]]);
                                }}
                              >
                                {deviceChartTypeOptions.map((opt, index) => (
                                  <Option key={index} value={opt.value}>
                                    {opt.label}
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={24} md={24} lg={24} xl={12} style={{ paddingRight: 0, paddingLeft: 0 }}>
                            <Form.Item
                              {...field}
                              label="Parameter"
                              name={[field.name, "parameter"]}
                              rules={[
                                { required: true, message: "required" },
                                {
                                  validator: async (_, value) => {
                                    return chartNumberValidator(deviceChartType?.[field.key], value.length);
                                  },
                                },
                              ]}
                              // validateStatus={deviceChartValidation?.[field.key]?.validateStatus || "success"}
                              // help={deviceChartValidation?.[field.key]?.errorMsg}
                            >
                              <Select
                                placeholder="Select"
                                mode="multiple"
                                maxTagCount="responsive"
                                // onChange={(value: keyof typeof parameterOptions) => {
                                //   setDeviceChartValidation({
                                //     ...deviceChartValidation,
                                //     [field.key]: validateChartNumber(deviceChartType[field.key] || "bar", value.length),
                                //   });
                                // }}
                              >
                                {parameterOptions[deviceChartType?.[field.key] || "bar"]?.map((option, index) => (
                                  <Option key={index} value={option.value}>
                                    {option.label}
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={24} md={24} lg={24} xl={1} className="mb-2 pr-0 pl-0">
                            <Button
                              icon={<MinusCircleOutlined />}
                              block
                              danger
                              type="text"
                              onClick={() => remove(field.name)}
                            ></Button>
                          </Col>
                        </Row>
                      ))}

                      <Form.Item>
                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                          Add Page
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form.List>
              </Col>
              {/* <Col xs={24} sm={24} md={24} lg={24} xl={7}>
                <h2>Email</h2>
                <Divider />


              </Col> */}
            </Row>
          </div>
        </Form>
      </Drawer>
    </>
  );
};

export default ReportTemplate;
